import {
  MAX_HORIZON_YEARS,
  MIN_HORIZON_YEARS,
  MIN_PACKAGES,
  PACKAGE_COLOR_SLOTS,
} from "@shared/constants";
import { blankLineItem, blankPackage, blankYear } from "@shared/roi/defaults";
import { largestRemainder } from "@shared/roi/engine";
import type { ScenarioDetail } from "@shared/schemas/scenario";
import { sameShape } from "@client/lib/stableJson";

/**
 * The editor's draft and every way it can change.
 *
 * A reducer rather than a dozen `useState`s because almost no edit here touches
 * one field: adding a package seeds a mix entry in every year, deleting one
 * clears those entries and any line-item restriction naming it, and shortening
 * the horizon drops year rows and clips the line items that ran into them. Those
 * are invariants of the model rather than of a component, so they live in one
 * pure function that a test can drive without rendering anything.
 *
 * The draft is the **wire** model (`ScenarioDetail["model"]`), not the engine's
 * `ScenarioModel`: it carries fields the engine ignores — a line item's note,
 * `yearIndex` — and dropping them on the way through the editor would mean a
 * save silently deleted them. `toScenarioModel` narrows it for the projection.
 */

export type EditorModel = ScenarioDetail["model"];
export type EditorPackage = EditorModel["packages"][number];
export type EditorLineItem = EditorModel["lineItems"][number];
export type EditorYear = EditorModel["years"][number];

export interface DraftState {
  model: EditorModel;
  /** The version this draft was built from; what a save is checked against. */
  version: number;
  /**
   * Set by every edit, cleared by `loaded`. Deliberately not a deep comparison
   * against the server's model: typing "5" over "5" leaves the model identical
   * but the user has still touched the page, and a save that writes the same
   * bytes costs less than a diff on every keystroke.
   */
  dirty: boolean;
}

export type DraftAction =
  /** Replace the whole draft — first load, a save's response, "take theirs". */
  | { type: "loaded"; model: EditorModel; version: number }
  /** Replace the model but keep it dirty — restoring the localStorage mirror. */
  | { type: "restored"; model: EditorModel; version: number }
  | {
      type: "settings";
      patch: Partial<Pick<EditorModel, "commitmentYears" | "discountRatePct" | "reuseAssets">>;
    }
  | { type: "horizon"; years: number }
  | { type: "addPackage" }
  | { type: "removePackage"; id: string }
  | { type: "movePackage"; id: string; delta: number }
  | { type: "patchPackage"; id: string; patch: Partial<EditorPackage> }
  | { type: "addLineItem"; patch?: Partial<EditorLineItem> }
  | { type: "removeLineItem"; id: string }
  | { type: "patchLineItem"; id: string; patch: Partial<EditorLineItem> }
  | { type: "patchYear"; index: number; patch: Partial<Omit<EditorYear, "mix">> }
  | { type: "setMix"; index: number; packageId: string; count: number };

/* ------------------------------------------------------------------ helpers */

const clampInt = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, Math.round(Number.isFinite(value) ? value : min)));

/**
 * An id no package or line item is already using.
 *
 * The server hands back cuids and `blankPackage` produces `pkg-1`, so in
 * practice the two never meet — but a scenario forked from one built in the
 * editor carries `pkg-1` already, and two packages sharing an id is the one
 * thing `scenarioModelSchema` refuses outright.
 */
function freshId(make: (index: number) => string, taken: ReadonlySet<string>): string {
  for (let index = 0; index < 1000; index += 1) {
    const candidate = make(index);
    if (!taken.has(candidate)) return candidate;
  }
  /* c8 ignore next 2 -- a thousand collisions means something else is very wrong */
  return `${make(0)}-${Date.now()}`;
}

/** The lowest colour slot nobody is using, so two new packages never match. */
function freshColorIndex(packages: readonly EditorPackage[]): number {
  const taken = new Set(packages.map((pkg) => pkg.colorIndex));
  for (let slot = 0; slot < PACKAGE_COLOR_SLOTS; slot += 1) {
    if (!taken.has(slot)) return slot;
  }
  return packages.length % PACKAGE_COLOR_SLOTS;
}

/** `sortOrder` is the array position; anything that reorders must say so. */
function resequence<T extends { sortOrder: number }>(items: T[]): T[] {
  return items.map((item, index) =>
    item.sortOrder === index ? item : { ...item, sortOrder: index },
  );
}

function moveBy<T>(items: T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (index < 0 || target < 0 || target >= items.length) return items;
  const next = [...items];
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved!);
  return next;
}

const mixTotal = (mix: Record<string, number>): number =>
  Object.values(mix).reduce((sum, count) => sum + count, 0);

/**
 * Keeps a year's split inside the signings it is splitting.
 *
 * When the total already fits, the mix comes back untouched. When it does not —
 * which only happens because `newClients` came down under an existing split —
 * the same `largestRemainder` the engine uses rescales it, so the *shape* of the
 * split survives and the rounding lands where the engine would put it. Letting a
 * mix sum past `newClients` would sign more clients than the year says it
 * signed, and no warning would ever mention it.
 */
export function clampMixToTotal(
  mix: Record<string, number>,
  packageIds: readonly string[],
  newClients: number,
): Record<string, number> {
  if (mixTotal(mix) <= newClients) return mix;
  return largestRemainder(newClients, mix, [...packageIds]);
}

/** What is left for one package once the others have taken their share. */
export function mixHeadroom(year: EditorYear, packageId: string): number {
  let others = 0;
  for (const [id, count] of Object.entries(year.mix)) {
    if (id !== packageId) others += count;
  }
  return Math.max(0, year.newClients - others);
}

/** Clients the split has not allocated. Earns nothing — the engine warns about it. */
export function unassignedIn(year: EditorYear): number {
  return Math.max(0, year.newClients - mixTotal(year.mix));
}

/** A mix holding an explicit entry for every package and nothing else. */
function alignMix(
  mix: Record<string, number>,
  packageIds: readonly string[],
): Record<string, number> {
  const next: Record<string, number> = {};
  for (const id of packageIds) next[id] = mix[id] ?? 0;
  return next;
}

/* ------------------------------------------------------------------ reducer */

function withPackages(model: EditorModel, packages: EditorPackage[]): EditorModel {
  const ids = packages.map((pkg) => pkg.id);
  return {
    ...model,
    packages,
    // A restriction naming a deleted package counts nothing, which reads on
    // screen as "restricted to something invisible". Dropping it says what happened.
    lineItems: model.lineItems.map((item) => {
      const kept = item.packageIds.filter((id) => ids.includes(id));
      return kept.length === item.packageIds.length ? item : { ...item, packageIds: kept };
    }),
    years: model.years.map((year) => {
      const mix = alignMix(year.mix, ids);
      return { ...year, mix: clampMixToTotal(mix, ids, year.newClients) };
    }),
  };
}

/**
 * The shortest horizon this scenario is allowed to have.
 *
 * A commitment is a promise that clients cannot leave for that many years, so a
 * horizon that stops before it ends is a projection that never reaches the first
 * year the commitment has anything to say about — every figure in it is the
 * locked-in period and nothing else. `MIN_HORIZON_YEARS` is the floor under
 * that, for a scenario whose commitment is none or a single year.
 *
 * Exported because the two controls that change the horizon — the dropdown in
 * scenario settings and "Remove year" in the adoption panel — both have to show
 * the same floor rather than let someone pick a value this then clamps.
 */
export function minHorizonFor(model: EditorModel): number {
  return Math.max(MIN_HORIZON_YEARS, model.commitmentYears);
}

function withHorizon(model: EditorModel, requested: number): EditorModel {
  const horizonYears = clampInt(requested, minHorizonFor(model), MAX_HORIZON_YEARS);
  if (horizonYears === model.horizonYears && model.years.length === horizonYears) return model;

  const packageIds = model.packages.map((pkg) => pkg.id);
  const years =
    horizonYears <= model.years.length
      ? model.years.slice(0, horizonYears)
      : [
          ...model.years,
          ...Array.from({ length: horizonYears - model.years.length }, () => blankYear(packageIds)),
        ];

  return {
    ...model,
    horizonYears,
    years: years.map((year, index) => ({ ...year, yearIndex: index + 1 })),
    // A line item reaching past the horizon fails the model schema, so shortening
    // the horizon has to bring the ranges with it rather than wait for the save
    // to come back complaining about year 7 in a five-year scenario.
    lineItems: model.lineItems.map((item) => {
      const startYear = Math.min(item.startYear, horizonYears);
      const endYear = item.endYear === null ? null : Math.min(item.endYear, horizonYears);
      if (startYear === item.startYear && endYear === item.endYear) return item;
      return {
        ...item,
        startYear,
        endYear: endYear === null ? null : Math.max(endYear, startYear),
      };
    }),
  };
}

export function draftReducer(state: DraftState, action: DraftAction): DraftState {
  if (action.type === "loaded") {
    return { model: action.model, version: action.version, dirty: false };
  }
  if (action.type === "restored") {
    return { model: action.model, version: action.version, dirty: true };
  }

  const next = applyEdit(state.model, action);
  // Identity, not equality: every branch below returns the model it was given
  // when nothing changed, so a clamped-to-the-same-value keystroke does not mark
  // the page dirty and schedule a save that would write nothing.
  if (next === state.model) return state;
  return { ...state, model: next, dirty: true };
}

function applyEdit(
  model: EditorModel,
  action: Exclude<DraftAction, { type: "loaded" | "restored" }>,
): EditorModel {
  switch (action.type) {
    case "settings":
      return { ...model, ...action.patch };

    case "horizon":
      return withHorizon(model, action.years);

    case "addPackage": {
      const taken = new Set(model.packages.map((pkg) => pkg.id));
      const seed = blankPackage(model.packages.length);
      const added: EditorPackage = {
        ...seed,
        id: freshId((index) => `pkg-${model.packages.length + index + 1}`, taken),
        sortOrder: model.packages.length,
        colorIndex: freshColorIndex(model.packages),
        name: uniqueName(seed.name, model.packages),
        maintenanceStartYear: 2,
      };
      return withPackages(model, [...model.packages, added]);
    }

    case "removePackage": {
      // The schema requires one package and the engine has nothing to project
      // without one, so the last one stays.
      if (model.packages.length <= MIN_PACKAGES) return model;
      const packages = model.packages.filter((pkg) => pkg.id !== action.id);
      if (packages.length === model.packages.length) return model;
      return withPackages(model, resequence(packages));
    }

    case "movePackage": {
      const index = model.packages.findIndex((pkg) => pkg.id === action.id);
      const moved = moveBy(model.packages, index, action.delta);
      if (moved === model.packages) return model;
      return { ...model, packages: resequence(moved) };
    }

    case "patchPackage": {
      let touched = false;
      const packages = model.packages.map((pkg) => {
        if (pkg.id !== action.id) return pkg;
        touched = true;
        // The fields a billing model does not read keep their values, so
        // switching back and forth in the picker is lossless — see PackageInput.
        return { ...pkg, ...action.patch };
      });
      return touched ? { ...model, packages } : model;
    }

    case "addLineItem": {
      const taken = new Set(model.lineItems.map((item) => item.id));
      const added: EditorLineItem = {
        ...blankLineItem(model.lineItems.length),
        id: freshId((index) => `li-${model.lineItems.length + index + 1}`, taken),
        sortOrder: model.lineItems.length,
        ...action.patch,
      };
      return { ...model, lineItems: [...model.lineItems, added] };
    }

    case "removeLineItem": {
      const lineItems = model.lineItems.filter((item) => item.id !== action.id);
      if (lineItems.length === model.lineItems.length) return model;
      return { ...model, lineItems: resequence(lineItems) };
    }

    case "patchLineItem": {
      let touched = false;
      const lineItems = model.lineItems.map((item) => {
        if (item.id !== action.id) return item;
        touched = true;
        const merged = { ...item, ...action.patch };
        // An end before its start is the one range the schema refuses, and both
        // ends can reach it. Whichever end was just moved is the one to keep.
        if (merged.endYear !== null && merged.endYear < merged.startYear) {
          return action.patch.startYear !== undefined
            ? { ...merged, endYear: merged.startYear }
            : { ...merged, startYear: merged.endYear };
        }
        return merged;
      });
      return touched ? { ...model, lineItems } : model;
    }

    case "patchYear": {
      const packageIds = model.packages.map((pkg) => pkg.id);
      const years = model.years.map((year, index) => {
        if (index !== action.index) return year;
        const merged = { ...year, ...action.patch };
        return { ...merged, mix: clampMixToTotal(merged.mix, packageIds, merged.newClients) };
      });
      return { ...model, years };
    }

    case "setMix": {
      let touched = false;
      const years = model.years.map((year, index) => {
        if (index !== action.index) return year;
        const wanted = Math.max(0, Math.round(action.count));
        const capped = Math.min(wanted, mixHeadroom(year, action.packageId));
        if (year.mix[action.packageId] === capped) return year;
        touched = true;
        return { ...year, mix: { ...year.mix, [action.packageId]: capped } };
      });
      return touched ? { ...model, years } : model;
    }
  }
}

/** "Package 4", then "Package 4 (2)" — never two the picker cannot tell apart. */
function uniqueName(seed: string, packages: readonly EditorPackage[]): string {
  const taken = new Set(packages.map((pkg) => pkg.name.trim().toLowerCase()));
  if (!taken.has(seed.trim().toLowerCase())) return seed;
  for (let suffix = 2; suffix < 100; suffix += 1) {
    const candidate = `${seed} (${suffix})`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
  /* c8 ignore next 2 */
  return seed;
}

/* ------------------------------------------------------------- id handover */

/**
 * Carries the server's ids into a draft that moved on while the save was in
 * flight.
 *
 * The server owns ids (see `scenarioWrite.ts`): a package the browser invented
 * as `pkg-2` comes back as a cuid, and saving `pkg-2` a second time would create
 * a *second* package. So the response's ids must reach the draft — but the draft
 * may have gained a keystroke since the request left, and adopting the response
 * wholesale would throw that keystroke away.
 *
 * `sent` and `stored` describe the same collections in the same order, because
 * the server writes what it was given in `sortOrder`, so position maps one to
 * the other. Anything added after the request left is not in `sent`, keeps its
 * draft id, and gets a real one on the next save.
 */
export function reconcileIds(
  current: EditorModel,
  sent: EditorModel,
  stored: EditorModel,
): EditorModel {
  const packageIds = pairIds(sent.packages, stored.packages);
  const lineItemIds = pairIds(sent.lineItems, stored.lineItems);
  if (packageIds.size === 0 && lineItemIds.size === 0) return current;

  const translate = (map: Map<string, string>, id: string): string => map.get(id) ?? id;

  return {
    ...current,
    packages: current.packages.map((pkg) => ({ ...pkg, id: translate(packageIds, pkg.id) })),
    lineItems: current.lineItems.map((item) => ({
      ...item,
      id: translate(lineItemIds, item.id),
      packageIds: item.packageIds.map((id) => translate(packageIds, id)),
    })),
    years: current.years.map((year) => {
      const mix: Record<string, number> = {};
      for (const [id, count] of Object.entries(year.mix)) mix[translate(packageIds, id)] = count;
      return { ...year, mix };
    }),
  };
}

/** Position-paired ids, and nothing at all if the two lists disagree on length. */
function pairIds(
  sent: readonly { id: string }[],
  stored: readonly { id: string }[],
): Map<string, string> {
  const map = new Map<string, string>();
  if (sent.length !== stored.length) return map;
  sent.forEach((entry, index) => {
    const counterpart = stored[index]!;
    if (entry.id !== counterpart.id) map.set(entry.id, counterpart.id);
  });
  return map;
}

/* ---------------------------------------------------------------- the diff */

/** The scalar settings, named so `String(...)` below cannot meet an array. */
type SettingKey = "horizonYears" | "commitmentYears" | "discountRatePct" | "reuseAssets";

const SETTINGS_LABELS: [SettingKey, string][] = [
  ["horizonYears", "Horizon"],
  ["commitmentYears", "Commitment"],
  ["discountRatePct", "Discount rate"],
  ["reuseAssets", "Reuse assets"],
];

/**
 * Plain sentences naming what two models disagree about, for the conflict dialog.
 *
 * Deliberately shallow: "Starter" changed, three line items became four. A
 * field-level diff is the version screen's job (phase 8); someone staring at a
 * conflict needs just enough to decide whose copy to keep.
 */
export function describeModelDifferences(mine: EditorModel, theirs: EditorModel): string[] {
  const notes: string[] = [];

  for (const [key, label] of SETTINGS_LABELS) {
    if (mine[key] !== theirs[key]) {
      notes.push(`${label} — yours ${String(mine[key])}, theirs ${String(theirs[key])}`);
    }
  }

  if (mine.packages.length !== theirs.packages.length) {
    notes.push(`Packages — yours ${mine.packages.length}, theirs ${theirs.packages.length}`);
  } else {
    const changed = mine.packages.filter((pkg) => {
      const other = theirs.packages.find((candidate) => candidate.id === pkg.id);
      return other === undefined || !sameShape(other, pkg);
    });
    if (changed.length > 0) {
      notes.push(`Packages changed — ${changed.map((pkg) => pkg.name).join(", ")}`);
    }
  }

  if (mine.lineItems.length !== theirs.lineItems.length) {
    notes.push(
      `Costs & revenues — yours ${mine.lineItems.length}, theirs ${theirs.lineItems.length}`,
    );
  } else if (!sameShape(mine.lineItems, theirs.lineItems)) {
    notes.push("Costs & revenues differ");
  }

  const myYears = mine.years.map((year) => year.newClients).join(", ");
  const theirYears = theirs.years.map((year) => year.newClients).join(", ");
  if (myYears !== theirYears) {
    notes.push(`New clients by year — yours ${myYears}; theirs ${theirYears}`);
  } else if (!sameShape(mine.years, theirs.years)) {
    notes.push("Adoption or churn differs");
  }

  if (notes.length === 0) {
    notes.push("Nothing the editor shows differs — the other save may have changed only a note.");
  }
  return notes;
}
